/*
 * Copyright (c) 2026
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

export type HolidayType = 'FIXED' | 'FLOATING' | 'ONE_OFF';

export class Holiday {
  ID: string;
  NAME: string;
  TYPE: HolidayType = 'ONE_OFF';
  MONTH: number;
  DAY: number;
  WEEK_OF_MONTH: number;
  DAY_OF_WEEK: number;
  OFFSET_DAYS = 0;
  HOLIDAY_DATE: string;
  OBSERVE_WEEKEND = false;
  ENABLED = true;

  static parse(json: any): Holiday {
    if (!json) return null;
    const h = new Holiday();
    h.ID = json.ID;
    h.NAME = json.NAME;
    h.TYPE = json.TYPE;
    h.MONTH = json.MONTH;
    h.DAY = json.DAY;
    h.WEEK_OF_MONTH = json.WEEK_OF_MONTH;
    h.DAY_OF_WEEK = json.DAY_OF_WEEK;
    h.OFFSET_DAYS = json.OFFSET_DAYS || 0;
    h.HOLIDAY_DATE = json.HOLIDAY_DATE;
    h.OBSERVE_WEEKEND = !!json.OBSERVE_WEEKEND;
    h.ENABLED = json.ENABLED === undefined ? true : !!json.ENABLED;
    return h;
  }

  static parseArray(json: any[]): Holiday[] {
    const arr: Holiday[] = [];
    if (json)
      for (const j of json)
        if (j) arr.push(Holiday.parse(j));
    return arr;
  }

  /** Human-readable description of when this holiday occurs, for the list view. */
  get scheduleText(): string {
    const months = ['', 'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    const weekdays = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const ordinals: { [k: number]: string } = { 1: '1st', 2: '2nd', 3: '3rd', 4: '4th', [-1]: 'last' };

    if (this.TYPE === 'ONE_OFF')
      return this.HOLIDAY_DATE ? ('' + this.HOLIDAY_DATE).substring(0, 10) : '';
    if (this.TYPE === 'FIXED')
      return `${months[this.MONTH]} ${this.DAY} (every year)`;
    if (this.TYPE === 'FLOATING') {
      let txt = `${ordinals[this.WEEK_OF_MONTH] || this.WEEK_OF_MONTH} ${weekdays[this.DAY_OF_WEEK]} of ${months[this.MONTH]}`;
      if (this.OFFSET_DAYS) txt += ` + ${this.OFFSET_DAYS} day(s)`;
      return txt;
    }
    return '';
  }
}
